<template>
  <div :aria-busy="isLoading" class="pt-32 md:pt-64 pb-72">
    <div aria-live="polite" class="sr-only" role="status">{{ liveStatus }}</div>
    <Skeleton v-if="isLoading" />
    <div v-else>
      <RevContainer>
        <div class="mx-auto max-w-[736px]">
          <p id="comparison-eyebrow" class="punchline-sans text-center">
            {{ i18n(translations.pageTitle) }}
          </p>
          <h1
            aria-describedby="comparison-eyebrow"
            class="heading-3 text-static-default-low mt-16 text-center"
          >
            {{ title }}
          </h1>
          <div v-if="category" class="mt-16 flex justify-center">
            <RevButton
              :to="category.url"
              variant="primary"
              @click="onCategoryClick"
            >
              {{
                i18n(translations.shopCategory, { category: category.label })
              }}
            </RevButton>
          </div>
          <Reassurance class="mt-56 text-center" />

          <div class="mx-auto mt-56">
            <div
              class="grid grid-cols-2 gap-x-24 lg:gap-x-40"
              :inert="isStickyVisible"
            >
              <div
                v-for="(product, index) in products"
                :key="`card-${product.productId}`"
              >
                <ProductSwitcher
                  :id="`product-switcher-${index}`"
                  class="mb-24"
                  :label="product.title"
                  :options="optionsPerProduct[index] ?? []"
                  @switch="onSwitchProduct(index, $event)"
                />
                <ProductCard
                  :product
                  @product-click="onProductClick(index, $event, 'compare_hero')"
                />
              </div>
            </div>
            <div ref="tabsSentinel">
              <Tabs :anchors class="mt-32" />
            </div>
            <TableContent
              ref="bottomSentinel"
              class="mt-32"
              :product-names="comparedProductNames"
              :ratings
              :table-data
              @reviews-click="onReviewsClick"
            />
          </div>
        </div>
      </RevContainer>
      <RevContainer class="fixed top-0 z-[1] inset-x-0">
        <div class="mx-auto max-w-[736px]">
          <Transition
            enter-active-class="motion-safe:ease-out motion-safe:duration-150"
            enter-from-class="motion-safe:-translate-y-full"
            enter-to-class="motion-safe:translate-y-0"
            leave-active-class="motion-safe:ease-in motion-safe:duration-150"
            leave-from-class="motion-safe:translate-y-0"
            leave-to-class="motion-safe:-translate-y-full"
          >
            <div
              v-show="isStickyVisible"
              :aria-label="i18n(translations.stickyHeader)"
              class="bg-surface-default-low"
              role="region"
            >
              <div
                class="mx-auto grid grid-cols-2 gap-x-24 pb-12 pt-16 lg:gap-x-40 lg:pb-16"
              >
                <div
                  v-for="(product, index) in products"
                  :key="`sticky-card-${product.productId}`"
                >
                  <ProductSwitcher
                    :id="`product-switcher-sticky-${index}`"
                    class="mb-16"
                    :label="product.title"
                    :options="optionsPerProduct[index] ?? []"
                    @switch="onSwitchProduct(index, $event)"
                  />
                  <ProductCard
                    :product
                    sticky
                    @product-click="
                      onProductClick(index, $event, 'compare_sticky_header')
                    "
                  />
                </div>
              </div>
              <!-- overflow-hidden + -mb-8 clip RevTabs' internal bottom margin
                   (added to align its border pseudo-element) without trapping
                   the ProductSwitcher dropdown, which opens downward. Hidden on
                   mobile where there is not enough vertical space.
                   v-if (not v-show) so RevTabs reads real offsetLeft / offsetWidth
                   for its active-bar on mount — both are 0 while display:none. -->
              <div class="hidden overflow-hidden md:block">
                <Tabs v-if="isStickyVisible" :anchors class="-mb-8" />
              </div>
            </div>
          </Transition>
        </div>
      </RevContainer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from '#imports'
import { computed, ref, toRef, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { useIntersectionObserver, useWindowScroll } from '@vueuse/core'

import { useHideOnScroll } from '~/scopes/navigation/composables/useHideOnScroll'
import { useAddCanonicalLink } from '~/scopes/seo/composables/useAddCanonicalLink'

import { useCompareTracking } from '../../../composables/useCompareTracking/useCompareTracking'
import { useComparisonOptions } from '../../../composables/useComparisonOptions/useComparisonOptions'
import { useEnrichResultsPage } from '../../../composables/useEnrichResultsPage/useEnrichResultsPage'
import type { ComparisonData } from '../../../composables/useEnrichResultsPage/useEnrichResultsPage.types'
import { useModelsComparisonHead } from '../../../composables/useModelsComparisonHeadV2/useModelsComparisonHeadV2'
import { MODELS_COMPARISON } from '../../../routes-names'
import { getCategorySlug } from '../../../utils/compareTracking'
import ProductCard from '../ProductCard/ProductCard.vue'
import ProductSwitcher from '../ProductSwitcher/ProductSwitcher.vue'
import Reassurance from '../Reassurance/Reassurance.vue'
import Skeleton from '../Skeleton/Skeleton.vue'
import TableContent from '../TableContent/TableContent.vue'
import Tabs from '../Tabs/Tabs.vue'

import translations from './Main.translations'

const props = defineProps<{
  comparisonData: ComparisonData | null | undefined
  comparisonPending: boolean
}>()

const route = useRoute()
const router = useRouter()

const { products, ratings, tableData, anchors, pending, category } =
  useEnrichResultsPage(
    toRef(props, 'comparisonData'),
    toRef(props, 'comparisonPending'),
  )

const slugA = computed(() => String(route.params.productA))
const slugB = computed(() => String(route.params.productB))
const { optionsPerProduct } = useComparisonOptions(slugA, slugB)

const tracking = useTracking()
const { withCompareContext } = useCompareTracking({
  products,
  category,
  slugA,
  slugB,
})

function onProductClick(
  index: number,
  payload: { ctaLabel: 'see_options' },
  listName: 'compare_hero' | 'compare_sticky_header',
) {
  const product = products.value[index]
  if (!product) return

  tracking.trackCompareProductClicked(
    withCompareContext({
      list_name: listName,
      position: index + 1,
      cta_label: payload.ctaLabel,
      product_id: product.productId,
      product_model: product.title,
      product_category: getCategorySlug(category.value?.url),
      product_price: product.price?.amount,
      product_price_new: product.priceWhenNew?.amount,
    }),
  )
}

function onCategoryClick() {
  const cat = category.value
  if (!cat) return

  tracking.trackClick(
    withCompareContext({
      name: 'plp_link',
      zone: 'compare_hero_cta',
      target_url: cat.url,
      target_category: getCategorySlug(cat.url),
    }),
  )
}

function onReviewsClick({ position }: { position: number }) {
  const product = products.value[position - 1]
  const rating = ratings.value[position - 1]
  if (!product || !rating) return

  tracking.trackClick(
    withCompareContext({
      name: 'reviews_link',
      zone: 'compare_hero',
      position,
      product_id: product.productId,
      product_model: product.title,
      product_rating: rating.averageRate,
      product_nb_reviews: rating.count,
    }),
  )
}

const { hidden: isHeaderHidden } = useHideOnScroll()

const i18n = useI18n()
const isScrolled = ref(false)
const isAtBottom = ref(false)
const tabsSentinel = useTemplateRef<HTMLElement>('tabsSentinel')
const bottomSentinel = useTemplateRef<{ $el: HTMLElement }>('bottomSentinel')

const title = computed(() =>
  i18n(translations.resultsTitle, {
    firstModel: products.value[0]?.title,
    secondModel: products.value[1]?.title,
  }),
)

const isLoading = computed(() => pending.value || !products.value.length)

const isStickyVisible = computed(
  () => isScrolled.value && !isAtBottom.value && isHeaderHidden.value,
)

const comparedProductNames = computed(() => products.value.map((p) => p.title))

const liveStatus = ref('')
watch(isLoading, (loading) => {
  liveStatus.value = loading
    ? i18n(translations.loadingComparison)
    : i18n(translations.comparisonLoaded)
})

function onSwitchProduct(index: number, option: { value: string }) {
  const newSlugA = index === 0 ? option.value : String(route.params.productA)
  const newSlugB = index === 1 ? option.value : String(route.params.productB)

  const previousProduct = products.value[index]
  tracking.trackClick(
    withCompareContext({
      name: 'product_switcher',
      zone: 'compare_switcher',
      position: index + 1,
      previous_product_slug: previousProduct?.slug,
      previous_product_id: previousProduct?.productId,
      new_product_slug: option.value,
    }),
  )

  router.push({
    name: MODELS_COMPARISON.COMPARISON_RESULTS,
    params: {
      locale: route.params.locale,
      productA: newSlugA,
      productB: newSlugB,
    },
  })
}

useIntersectionObserver(tabsSentinel, ([{ isIntersecting }]) => {
  isScrolled.value = !isIntersecting
})

const { y: scrollY } = useWindowScroll()
watch(scrollY, () => {
  const el = bottomSentinel.value?.$el
  if (!el) return
  // Hide the sticky once the table's bottom reaches the sticky's bottom —
  // there's no more table content for the sticky to anchor to. Heights
  // mirror the scroll-mt-* on the comparison headings (see TableContent.vue).
  const stickyHeight = window.matchMedia('(min-width: 768px)').matches
    ? 202
    : 196
  isAtBottom.value = el.getBoundingClientRect().bottom <= stickyHeight
})

const productNames = computed(() =>
  products.value.length >= 2
    ? [products.value[0].title, products.value[1].title]
    : null,
)
useModelsComparisonHead(productNames)

useAddCanonicalLink()
</script>
